import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, Divider, Button, Link,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const LeadFlowEmailBranding: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="green">LeadFlow</Badge>
              <Badge colorScheme="purple">v1.1.0</Badge>
              <Text fontSize="xs" color="gray.400">26 Apr 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">
              LeadFlow emails: your own skin, HTML bodies, live preview
            </Heading>
            <Text color="gray.600">
              Three changes so the person running waitlist, pledge, and welcome
              campaigns can fully own how those emails look — without trampling the
              tenant-wide email branding that bills, calendar reminders, and every
              other module rely on.
            </Text>
          </VStack>

          <Divider />

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>
            <VStack spacing={3} align="start">
              <Text>
                <strong>1. Per-module Email Branding settings page.</strong> A new
                page at <code>/leadflow/email-branding</code> lets you set a profile
                photo, signature image, banner, pen name, footer line, social links,
                CTA button, email style (Branded / Minimal / Personal), font, and
                brand colors — for LeadFlow emails specifically. Every field
                falls through to your tenant defaults if you leave it blank, so you
                only fill in what you want different for the campaign.
              </Text>
              <Text>
                <strong>2. HTML emails (opt-in per template).</strong> Action
                templates now have a "Body is HTML" switch in the editor. Flip it on
                and paste HTML directly — links, lists, bold, the lot. Existing
                plain-text templates keep working unchanged.
              </Text>
              <Text>
                <strong>3. Live HTML preview, server-rendered.</strong> The settings
                page has a sticky preview pane on the right that updates as you
                tweak. The lead-detail send panel now has Edit / Preview tabs —
                Preview shows the same wrapped HTML the recipient will see in their
                inbox, with your branding applied.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How to Use</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>
                Open the LeadFlow nav and click <strong>🎨 Email Branding</strong>
                {" "}(or visit <code>/leadflow/email-branding</code> directly).
              </ListItem>
              <ListItem>
                Upload a profile photo, set your pen name (e.g. "Anna x"), and a
                footer line.
              </ListItem>
              <ListItem>
                Fill in any social links you want — icons appear in the email
                footer.
              </ListItem>
              <ListItem>
                Pick an email style:{" "}
                <strong>Branded</strong> for a hero header + footer,{" "}
                <strong>Minimal</strong> for a clean card,{" "}
                <strong>Personal</strong> for a Gmail-like 1:1 feel.
              </ListItem>
              <ListItem>
                Watch the live preview on the right update as you tweak. Save when
                happy.
              </ListItem>
              <ListItem>
                Open any lead, pick an action template, switch to the{" "}
                <strong>Preview</strong> tab — that's exactly what they'll receive.
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try It Now!</Heading>
            <Text mb={3}>
              Open your LeadFlow Email Branding page, drop in a profile photo and
              a pen name, switch the style to Personal, and send a test welcome
              email to yourself.
            </Text>
            <Link as={RouterLink} to="/leadflow/email-branding" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="green" size="md" rightIcon={<FiArrowRight />}>
                Open Email Branding
              </Button>
            </Link>
          </Box>

          <Box>
            <Text>
              As always, reach out if anything's behaving oddly — happy to jump on a
              quick call.
            </Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default LeadFlowEmailBranding;
